import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {C, btn} from './theme';
import {flatIndex, inkOn, sceneKey, sceneLabel, sections} from './describe';
import {CueInspector, cueSpans, cueSwatch, newCue, useEffectCatalog} from './LightsLane';
import {layoutBlock, songClips} from './segments';

// The show on one time axis. A block is as long as its music (one clip, or a medley); blocks without
// music yet get a nominal length per scene and are drawn hatched. Scenes sit at their marker times
// inside the block. Zoom (pinch / ⌘+scroll) is continuous; detail appears as there's room for it.
//   mode 'compact' — read-only: thumbnails + a thin waveform strip per block (the Show tab)
//   mode 'edit'    — adds lanes under every block: music (drop clips here), scene markers (drag),
//                    cue lanes, and transition chips on the seams between blocks (the Edit tab)
export const NOMINAL_SCENE_SECONDS = 6;
export const LANE_ROWS = [
    {id: 'music', label: 'Music', h: 96},
    {id: 'markers', label: 'Scenes', h: 30},
    {id: 'sfx', label: 'SFX', h: 40},
    {id: 'voice', label: 'Voice', h: 40},
    {id: 'lights', label: 'Lights', h: 54},
    {id: 'screen', label: 'Screen', h: 40},
];
const SEAM = 3, FOLDER_GAP = 14, MIN_PPS = 0.05;
const SCENE_GAP = 6;        // scenes in a block sit as separate cards, this far apart
const MUSIC_END_NEXT = {stop: 'loop', loop: 'next', next: 'stop'};
const MUSIC_END_LABEL = {stop: '■ then stop', loop: '⟳ loop', next: '→ then next'};
const MUSIC_END_TITLE = {stop: 'When the music ends, it stops', loop: 'The music loops until the show moves on', next: 'When the music ends, the show moves to the next block'};
const TRANSITION_CYCLE = [{type: 'cut'}, {type: 'fade', seconds: 1}, {type: 'crossfade', seconds: 2}];

// A block's media is a chain of segments (videos, songs — see segments.js); everything here reads its layout.
export const blockLayout = (doc, block) => layoutBlock(doc, block, {tailSeconds: NOMINAL_SCENE_SECONDS});
export const musicOf = (doc, block) => songClips(blockLayout(doc, block)).map(a => a.clip);
export const hasMedia = (doc, block) => !!blockLayout(doc, block);
export const sceneStarts = (doc, block) => {
    const L = blockLayout(doc, block);
    return L ? L.starts : block.scenes.map((s, i) => i * NOMINAL_SCENE_SECONDS);
};
// Where a click at block time t lands: inside the live scene, right there; on any other scene, at its start.
// null: the live scene is waiting on the game, so there's no time in it to seek to.
export const seekLanding = (doc, live, b, t) => {
    const block = doc.blocks[b], starts = sceneStarts(doc, block), L = blockLayout(doc, block);
    const s = starts.reduce((cur, st, i) => st <= t + 1e-6 ? i : cur, 0);
    const isLive = !!live && live.blockIndex === b && live.sceneIndex === s, timed = !!L && !!L.timed[s];
    if (!isLive) return {s, t: starts[s], isLive, timed};
    return timed ? {s, t, isLive, timed} : null;
};
export const blockDuration = (doc, block) => {
    const L = blockLayout(doc, block);
    return L ? Math.max(L.duration, L.starts.at(-1) >= L.total ? L.starts.at(-1) + NOMINAL_SCENE_SECONDS : 0) : block.scenes.length * NOMINAL_SCENE_SECONDS;
};
// The beat grid of the block's songs, in block time: [{t, bar}] for every beat.
const beatsOf = (doc, block) => {
    const out = [];
    for (const {clip: c, at} of songClips(blockLayout(doc, block))) {
        const g = c.grid;
        if (!g) continue;
        const period = 60 / g.bpm;
        for (let k = Math.ceil((c.start - g.firstBeat) / period); ; k++) {
            const t = g.firstBeat + k * period;
            if (t > c.end) break;
            const rel = k - g.barPhase;
            out.push({t: at + t - c.start, bar: ((rel % g.beatsPerBar) + g.beatsPerBar) % g.beatsPerBar === 0, n: Math.floor(rel / g.beatsPerBar) + 1});
        }
    }
    return out;
};
const bpmAt = (doc, block, t) => {
    const items = songClips(blockLayout(doc, block));
    const a = items.find(x => t < x.at + x.length) || items.at(-1);
    return a && a.clip.grid ? a.clip.grid.bpm : 120;
};
const snapToBeat = (doc, block, t, free) => {
    const beats = beatsOf(doc, block);
    return beats.length && !free ? beats.reduce((best, b) => Math.abs(b.t - t) < Math.abs(best - t) ? b.t : best, beats[0].t) : t;
};
const fmtDur = (s) => s >= 60 ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : `${Math.round(s)}s`;

const TIMELINE_CSS = `
@keyframes tl-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .35; transform: scale(.7); } }
.tl-live-dot { animation: tl-pulse 1.4s ease-in-out infinite; }
.tl-strip::-webkit-scrollbar { height: 8px; }
.tl-strip::-webkit-scrollbar-thumb { background: #2a2f39; border-radius: 4px; }
.tl-drop { outline: 2px dashed ${C.accent}; outline-offset: -2px; }
.tl-clip .tl-fade, .tl-clip .tl-x { opacity: 0; transition: opacity .12s; }
.tl-clip:hover .tl-fade, .tl-clip:hover .tl-x { opacity: 1; }
.tl-clip:hover .tl-edge { background: ${C.accent}; }
.tl-scene:hover { outline: 2px solid #4da3ff; outline-offset: -2px; }
`;

// Waveform of everything a block sounds — its videos' audio and its songs — at their places on the block's clock.
// Drawn in tiles: zoomed in, a long block outgrows the largest canvas a browser will draw (it comes out blank white).
// Only tiles on screen or near it hold a canvas; the rest are empty space, so a deep zoom stays light.
const WAVE_TILE = 4096;
const useNearView = (ref) => {
    const [near, setNear] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(([e]) => setNear(e.isIntersecting), {root: el.closest('.tl-strip'), rootMargin: '0px 1500px'});
        io.observe(el);
        return () => io.disconnect();
    }, [ref]);
    return near;
};
const LazyWaveTile = (props) => {
    const box = useRef(null);
    const near = useNearView(box);
    return <div ref={box} style={{width: props.w, height: props.height, flex: 'none'}}>{near && <WaveTile {...props}/>}</div>;
};
const WaveTile = ({doc, block, analyses, width, height, color, grid, x0, w}) => {
    const ref = useRef(null);
    useEffect(() => {
        const cv = ref.current;
        const L = blockLayout(doc, block);
        if (!cv || width < 2 || !L) return;
        const dpr = window.devicePixelRatio || 1;
        cv.width = Math.max(1, Math.round(w * dpr)); cv.height = height * dpr;
        const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr); ctx.clearRect(0, 0, w, height); ctx.translate(-x0, 0);
        const pps = width / L.total, x1 = x0 + w;
        if (grid) {
            for (const b of beatsOf(doc, block)) {
                if (!b.bar && pps * 60 / 124 < 6) continue;
                const x = b.t * pps;
                if (x < x0 - 1 || x > x1) continue;
                ctx.fillStyle = b.bar ? 'rgba(255,255,255,.18)' : 'rgba(255,255,255,.06)';
                ctx.fillRect(x, 0, 1, height);
            }
        }
        // one column per pixel of the stretch at `at` (block seconds), its height the loudest peak under it
        const peaks = (a, at, from, length) => {
            ctx.fillStyle = color;
            const p0 = Math.max(0, Math.floor(x0 - at * pps)), p1 = Math.min(length * pps, Math.ceil(x1 - at * pps));
            for (let px = p0; px < p1; px++) {
                const t0 = from + px / pps, t1 = t0 + 1 / pps;
                let p = 0;
                for (let i = Math.floor(t0 * a.peaksPerSec); i < Math.ceil(t1 * a.peaksPerSec) && i < a.peaks.length; i++) if (a.peaks[i] > p) p = a.peaks[i];
                const h = Math.max(1, p * (height - 4));
                ctx.fillRect(at * pps + px, (height - h) / 2, 1, h);
            }
        };
        L.audio.filter(a => a.kind === 'video').forEach(({video, at, length}) => {
            const a = analyses.get(`video:${video}`);
            if (a && a.peaks) peaks(a, at, 0, length);
        });
        songClips(L).forEach(({clip: c, at, n}) => {
            const a = analyses.get(c.media);
            if (a && a.peaks) peaks(a, at, c.start, c.end - c.start);
            if (n > 0) { ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fillRect(at * pps, 0, 1, height); }
        });
    }, [doc, block, analyses, analyses.version, width, height, color, grid, x0, w]);
    return <canvas ref={ref} style={{width: w, height, display: 'block', flex: 'none'}}/>;
};

const MusicWave = (props) => {
    const tiles = [];
    for (let x0 = 0; x0 < props.width; x0 += WAVE_TILE) tiles.push(x0);
    return <div style={{display: 'flex', width: props.width}}>
        {tiles.map(x0 => <LazyWaveTile key={x0} {...props} x0={x0} w={Math.min(WAVE_TILE, props.width - x0)}/>)}
    </div>;
};

// A bed's stretch of the music lane: open-ended, it lasts until the game moves on (drawn at one pass of its playlist).
const BedSpan = ({layout, pps, height}) => layout.segments.filter(g => g.open).map(g =>
    <div key={g.k} title="Open: the playlist loops until the game moves on, then fades out" style={{position: 'absolute', top: 0, height, left: g.start * pps, width: g.dur * pps,
        pointerEvents: 'none', background: 'repeating-linear-gradient(135deg, rgba(255,255,255,.05) 0 6px, transparent 6px 14px)', borderLeft: '2px dashed rgba(255,255,255,.5)'}}>
        {g.dur * pps > 120 && <span style={{position: 'absolute', right: 6, bottom: 4, fontSize: 10, color: '#fff', textShadow: '0 1px 2px #000', whiteSpace: 'nowrap'}}>⟳ until the game moves on</span>}
    </div>);

// What the main screen shows while a video plays: the video's frames, laid over the scene cards for the part of
// the card the video covers, so the strip runs at the video's real pace across them.
const ScreenVideo = ({layout, from, to, pps}) => layout.segments.filter(g => g.kind === 'video').map(g => {
    const a = Math.max(from, g.start), b = Math.min(to, g.start + g.length);
    if (b <= a) return null;
    return <div key={g.k} title={`${g.video} · ${g.length.toFixed(1)} s`} style={{position: 'absolute', top: 0, bottom: 0, left: (a - from) * pps, width: (b - a) * pps,
        backgroundColor: '#000', backgroundImage: `url(/engine/stage-media/strip/${encodeURIComponent(g.video)}.jpg)`, backgroundRepeat: 'no-repeat',
        backgroundSize: `${g.length * pps}px 100%`, backgroundPosition: `${(g.start - a) * pps}px 0`}}/>;
});

// A line sweeping across the block that's playing, from the engine's start time.
// Playback that reached a bed keeps looping its playlist: the head goes round inside the bed.
const Playhead = ({playback, pps, layout}) => {
    const ref = useRef(null);
    useEffect(() => {
        let raf;
        const bed = layout && layout.segments.find(g => g.open && Math.abs(g.start + g.dur - playback.duration) < 0.05);
        const tick = () => {
            let t = (Date.now() - playback.startedAt) / 1000;
            if (bed && t > playback.duration) { const p0 = bed.playlistAt, len = bed.start + bed.dur - p0; t = len > 0 ? p0 + (t - p0) % len : p0; }
            if (ref.current) { ref.current.style.left = `${Math.max(0, t) * pps}px`; ref.current.style.opacity = t < 0 || t > playback.duration ? 0 : 1; }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [playback, pps, layout]);
    return <div ref={ref} style={{...headLine, background: '#fff', boxShadow: `0 0 0 1px #000c, 0 0 12px 2px ${C.accent}`}}><HeadCap color="#fff"/></div>;
};

// A playhead: a bright line through the block with a cap above it, outlined so it reads on any thumbnail.
const headLine = {position: 'absolute', top: -6, bottom: -6, width: 3, marginLeft: -1.5, zIndex: 6, pointerEvents: 'none'};
const HeadCap = ({color}) => <div style={{position: 'absolute', top: -9, left: -6.5, width: 16, height: 11, background: color,
    clipPath: 'polygon(0 0, 100% 0, 50% 100%)', filter: 'drop-shadow(0 0 1px #000)'}}/>;

const fmtClock = (s) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, '0')}`;
const songSnap = (clip, t, free) => {
    const g = clip.grid;
    if (!g || free) return t;
    const period = 60 / g.bpm;
    return g.firstBeat + Math.round((t - g.firstBeat) / period) * period;
};

// A clip on the music lane: drag the edges to trim (snaps to the song's beats, ⌥ for free), the corner
// squares to set fades, shift-drag the body to slide which part of the song plays. Click = playhead.
const ClipHandles = ({clip, x, w, h, onGrab, onRemove, active}) => {
    const fi = Math.min(w, (clip.fadeIn || 0) * (w / (clip.end - clip.start))), fo = Math.min(w, (clip.fadeOut || 0) * (w / (clip.end - clip.start)));
    const grab = (kind) => (e) => { e.preventDefault(); e.stopPropagation(); onGrab(kind, e); };
    const edge = {position: 'absolute', top: 0, bottom: 0, width: 8, cursor: 'ew-resize', zIndex: 3};
    const sq = {position: 'absolute', top: 1, width: 9, height: 9, marginLeft: -4.5, borderRadius: 2, background: '#fff', border: '1px solid #000', cursor: 'ew-resize', zIndex: 4};
    return <div className="tl-clip" onMouseDown={e => { if (e.shiftKey) grab('slip')(e); }}
        style={{position: 'absolute', left: x, width: w, top: 0, height: h, boxSizing: 'border-box', borderRadius: 4,
            boxShadow: active ? `inset 0 0 0 2px ${C.accent}` : 'inset 0 0 0 1px rgba(255,255,255,.12)'}}>
        <svg width={w} height={h} style={{position: 'absolute', inset: 0, pointerEvents: 'none'}}>
            {fi > 0 && <><polygon points={`0,0 ${fi},0 0,${h}`} fill="rgba(0,0,0,.6)"/><line x1={0} y1={h} x2={fi} y2={0} stroke="#fff" strokeWidth={1}/></>}
            {fo > 0 && <><polygon points={`${w},0 ${w - fo},0 ${w},${h}`} fill="rgba(0,0,0,.6)"/><line x1={w} y1={h} x2={w - fo} y2={0} stroke="#fff" strokeWidth={1}/></>}
        </svg>
        <div data-noseek="1" onMouseDown={grab('start')} title="Trim the start (snaps to beats, ⌥ for free)" style={{...edge, left: 0}}><div className="tl-edge" style={{position: 'absolute', left: 0, top: 0, bottom: 0, width: 3}}/></div>
        <div data-noseek="1" onMouseDown={grab('end')} title="Trim the end (snaps to beats, ⌥ for free)" style={{...edge, right: 0}}><div className="tl-edge" style={{position: 'absolute', right: 0, top: 0, bottom: 0, width: 3}}/></div>
        {w > 40 && <div data-noseek="1" className="tl-fade" onMouseDown={grab('fadeIn')} title="Fade in" style={{...sq, left: Math.max(5, fi)}}/>}
        {w > 40 && <div data-noseek="1" className="tl-fade" onMouseDown={grab('fadeOut')} title="Fade out" style={{...sq, left: Math.min(w - 5, w - fo)}}/>}
        {w > 70 && <span style={{position: 'absolute', left: 8, bottom: 3, fontSize: 9, color: '#fffc', textShadow: '0 1px 2px #000', whiteSpace: 'nowrap', pointerEvents: 'none'}}>
            {fmtClock(clip.start)} – {fmtClock(clip.end)} of the song</span>}
        {w > 60 && <button data-noseek="1" className="tl-x" onMouseDown={e => e.stopPropagation()} onClick={onRemove} title="Remove from this block" style={{position: 'absolute', right: 10, top: 3, zIndex: 4,
            fontSize: 10, lineHeight: '14px', padding: '0 5px', borderRadius: 4, border: `1px solid ${C.line}`, background: '#000a', color: '#fff', cursor: 'pointer'}}>✕</button>}
    </div>;
};

// A scene pinned to a moment in the music carries its seam line down through the music lane; a scene that isn't
// (waiting for the game in a bed, or after the music ends) only marks its card.
const MusicSeams = ({layout, starts, pps}) => starts.map((t, i) => i > 0 && layout.timed[i] && !layout.segmentOf(i).open && t < layout.total
    ? <div key={i} style={{position: 'absolute', left: t * pps - SCENE_GAP / 2, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,.6)', zIndex: 3, pointerEvents: 'none'}}/> : null);

// The parked playhead: where ▶ starts, and where a click put it.
const Cursor = ({t, pps}) => <div style={{...headLine, left: t * pps, background: '#ffffffcc', boxShadow: '0 0 0 1px #000a'}}><HeadCap color="#ffffffcc"/></div>;
const Ghost = React.forwardRef((_, ref) => <div ref={ref} style={{...headLine, background: '#ffffff66', pointerEvents: 'none', display: 'none'}}><HeadCap color="#ffffff66"/></div>);

export const Timeline = ({doc: docIn, analyses, selected, live, mode = 'compact', edits, thumbsStamp, scaleKey = 'tl.pps', playback = null, onAudition, cursor = null, onSeek}) => {
    const edit = mode === 'edit';
    const [trim, setTrim] = useState(null);                   // {b, k, clipId, kind, x0, orig, cur}
    const justDragged = useRef(false);
    const doc = useMemo(() => trim && trim.cur ? {...docIn, clips: {...docIn.clips, [trim.clipId]: {...docIn.clips[trim.clipId], ...trim.cur}}} : docIn, [docIn, trim]);
    const strip = useRef(null);
    const [stripHeight, setStripHeight] = useState(0);
    const [atEnd, setAtEnd] = useState(false);
    useEffect(() => {
        const el = strip.current;
        if (!el) return;
        const check = () => { setStripHeight(el.clientHeight); setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2); };
        const ro = new ResizeObserver(check);
        ro.observe(el);
        el.addEventListener('scroll', check, {passive: true});
        return () => { ro.disconnect(); el.removeEventListener('scroll', check); };
    }, []);
    const anchor = useRef(null);
    const [pps, setPps] = useState(() => { try { return Number(localStorage.getItem(scaleKey)) || 3; } catch { return 3; } });
    const [dropBlock, setDropBlock] = useState(null);
    const [dragMarker, setDragMarker] = useState(null);       // {block, i, t}
    const [error, setError] = useState(null);
    const [selCue, setSelCue] = useState(null);               // {block: index, id}
    const [dragCue, setDragCue] = useState(null);             // {block, id, x0, at0, t}
    const catalog = useEffectCatalog();
    useEffect(() => { try { localStorage.setItem(scaleKey, String(pps)); } catch {} }, [pps, scaleKey]);

    // Show time of every block (sections laid end to end).
    const layout = useMemo(() => {
        let t = 0;
        return doc.blocks.map(b => { const dur = blockDuration(doc, b); const r = {t0: t, dur, starts: sceneStarts(doc, b)}; t += dur; return r; });
    }, [doc]);
    const total = layout.length ? layout.at(-1).t0 + layout.at(-1).dur : 0;

    // Zoom around the show time under the cursor.
    useEffect(() => {
        const el = strip.current;
        const onWheel = (e) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            const blocks = [...el.querySelectorAll('[data-t0]')];
            let best = null, bestDist = Infinity;
            for (const n of blocks) {
                const r = n.getBoundingClientRect();
                const d = e.clientX < r.left ? r.left - e.clientX : e.clientX > r.right ? e.clientX - r.right : 0;
                if (d < bestDist) { bestDist = d; best = {n, r}; }
                if (!d) break;
            }
            if (best) anchor.current = {t: +best.n.dataset.t0 + Math.min(1, Math.max(0, (e.clientX - best.r.left) / best.r.width)) * +best.n.dataset.dur, x: e.clientX};
            const step = e.ctrlKey ? 0.01 : 0.0025;
            setPps(p => Math.min(120, Math.max(MIN_PPS, p * Math.exp(-e.deltaY * step))));
        };
        el.addEventListener('wheel', onWheel, {passive: false});
        return () => el.removeEventListener('wheel', onWheel);
    }, []);
    useLayoutEffect(() => {
        const a = anchor.current, el = strip.current;
        if (!a || !el) return;
        const n = [...el.querySelectorAll('[data-t0]')].find(b => a.t < +b.dataset.t0 + +b.dataset.dur) || el.querySelector('[data-t0]:last-of-type');
        if (!n) return;
        const r = n.getBoundingClientRect();
        el.scrollLeft += r.left + ((a.t - +n.dataset.t0) / +n.dataset.dur) * r.width - a.x;
    }, [pps]);
    // Zoomed out, the folders squeeze together: from the zoom where the shortest would reach its title width down to the
    // furthest zoom, each folder's scale moves (in log steps) from that zoom to the one that fills its title, all arriving at once.
    const tabs = useRef({}), blockPps = useRef({});
    const [tabWidths, setTabWidths] = useState({});
    useLayoutEffect(() => {
        const measure = () => {
            const m = {};
            Object.entries(tabs.current).forEach(([k, el]) => { if (el) m[k] = el.offsetWidth; });
            setTabWidths(w => JSON.stringify(w) === JSON.stringify(m) ? w : m);
        };
        measure();
        document.fonts && document.fonts.ready.then(measure);
    }, [doc]);
    const folderChrome = (sec) => 8 + SEAM * (sec.blocks.length - 1);       // the folder's padding, border and seams
    const folderPps = (() => {
        const secs = sections(doc), floors = {};
        secs.forEach(sec => {
            const dur = sec.blocks.reduce((s, b) => s + layout[b].dur, 0);
            floors[sec.blocks[0]] = dur > 0 ? Math.max(0, (tabWidths[sec.blocks[0]] || 0) - folderChrome(sec)) / dur : 0;
        });
        const hi = Math.max(MIN_PPS, ...Object.values(floors));
        if (pps >= hi) return () => pps;
        const z = Math.log(hi / pps) / Math.log(hi / MIN_PPS);
        return (k) => Math.max(floors[k], hi * Math.pow(Math.max(floors[k], 1e-6) / hi, z));
    })();
    useEffect(() => {
        const el = strip.current && strip.current.querySelector('[data-live="1"]');
        if (el) el.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'nearest'});
    }, [live]);

    // Scene markers: drag within the block, snapping to the music's beats.
    useEffect(() => {
        if (!dragMarker) return;
        const onMove = (e) => {
            const blockEl = strip.current.querySelector(`[data-block="${dragMarker.block}"]`);
            const r = blockEl.getBoundingClientRect(), block = doc.blocks[dragMarker.block];
            const dur = layout[dragMarker.block].dur, starts = layout[dragMarker.block].starts;
            const seg = blockLayout(doc, block).segmentOf(dragMarker.i);
            let t = Math.min(dur, Math.max(0, ((e.clientX - r.left) / r.width) * dur));
            const beats = beatsOf(doc, block);
            if (beats.length && !e.altKey) t = beats.reduce((best, b) => Math.abs(b.t - t) < Math.abs(best - t) ? b.t : best, beats[0].t);
            // a marker stays inside its own segment (a song's scenes on that song, a video's on that video)
            const lo = Math.max(seg.start, starts[dragMarker.i - 1] + 0.05), hi = Math.min(seg.start + seg.dur, dragMarker.i + 1 < starts.length ? starts[dragMarker.i + 1] - 0.05 : dur);
            setDragMarker(d => ({...d, t: Math.min(hi, Math.max(lo, t))}));
        };
        const onUp = () => {
            setDragMarker(d => {
                if (d && d.t != null) {
                    const block = doc.blocks[d.block], times = [...layout[d.block].starts];
                    times[d.i] = d.t;
                    edits.setSceneTimes(block.id, times).catch(e => setError(e.message));
                }
                return null;
            });
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [dragMarker && dragMarker.block, dragMarker && dragMarker.i]);   // eslint-disable-line react-hooks/exhaustive-deps

    const dropOnMusic = (b) => (e) => {
        e.preventDefault(); setDropBlock(null);
        const clip = e.dataTransfer.getData('application/x-clip'), song = e.dataTransfer.getData('application/x-song');
        const block = doc.blocks[b];
        if (song) { const {id, name} = JSON.parse(song); edits.addSong(block, id, name).catch(err => setError(err.message)); return; }
        if (!clip) return;
        edits.addClip(block, clip).catch(err => setError(err.message));
    };
    const cycleTransition = (block) => {
        const cur = block.transition ? TRANSITION_CYCLE.findIndex(t => t.type === block.transition.type) : 0;
        edits.patchBlock(block.id, {transition: TRANSITION_CYCLE[(cur + 1) % TRANSITION_CYCLE.length]}).catch(err => setError(err.message));
    };

    // Lights lane: drop effects from the shelf, drag cues along the beat grid, edit them in the inspector.
    const blockXToT = (b, clientX) => {
        const r = strip.current.querySelector(`[data-block="${b}"]`).getBoundingClientRect();
        return Math.min(layout[b].dur, Math.max(0, ((clientX - r.left) / r.width) * layout[b].dur));
    };
    const saveCues = (block, cues) => edits.patchBlock(block.id, {cues}).catch(err => setError(err.message));
    const dropOnLights = (b) => (e) => {
        e.preventDefault(); setDropBlock(null);
        const id = e.dataTransfer.getData('application/x-effect'), fx = catalog && catalog.effects.find(x => x.id === id);
        if (!fx) return;
        const block = doc.blocks[b], cue = newCue(fx, +snapToBeat(doc, block, blockXToT(b, e.clientX), e.altKey).toFixed(3));
        saveCues(block, [...(block.cues || []), cue]);
        setSelCue({block: b, id: cue.id});
    };
    const selectedCue = selCue && doc.blocks[selCue.block] && (doc.blocks[selCue.block].cues || []).find(c => c.id === selCue.id);
    const updateCue = (cue) => {
        const block = doc.blocks[selCue.block];
        saveCues(block, (block.cues || []).map(c => c.id === cue.id ? cue : c));
    };
    const deleteCue = () => {
        const block = doc.blocks[selCue.block];
        saveCues(block, (block.cues || []).filter(c => c.id !== selCue.id));
        setSelCue(null);
    };
    useEffect(() => {
        if (!selectedCue) return;
        const onKey = (e) => {
            if (e.target.closest && e.target.closest('input, textarea')) return;
            if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); deleteCue(); }
            if (e.key === 'Escape') setSelCue(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });
    useEffect(() => {
        if (!dragCue) return;
        const block = doc.blocks[dragCue.block];
        const onMove = (e) => {
            const raw = dragCue.at0 + (blockXToT(dragCue.block, e.clientX) - blockXToT(dragCue.block, dragCue.x0));
            setDragCue(d => ({...d, t: +snapToBeat(doc, block, Math.min(layout[d.block].dur, Math.max(0, raw)), e.altKey).toFixed(3)}));
        };
        const onUp = () => {
            setDragCue(d => {
                if (d && d.t != null && d.t !== d.at0) saveCues(block, (block.cues || []).map(c => c.id === d.id ? {...c, at: d.t} : c));
                return null;
            });
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [dragCue && dragCue.id]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Trimming: the drag edits a local copy of the clip (the lane reflows live); release saves it.
    const grabClip = (b, k, kind, e) => {
        const block = docIn.blocks[b], clipId = songClips(blockLayout(docIn, block))[k].clip.id, c = docIn.clips[clipId];
        setTrim({b, k, clipId, kind, x0: e.clientX, orig: {start: c.start, end: c.end, fadeIn: c.fadeIn || 0, fadeOut: c.fadeOut || 0}, cur: null});
    };
    useEffect(() => {
        if (!trim) return;
        const c = docIn.clips[trim.clipId], o = trim.orig, len = o.end - o.start;
        const a = analyses.get(c.media), songLen = (a && a.duration) || Infinity;
        const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
        const onMove = (e) => {
            const dt = (e.clientX - trim.x0) / (blockPps.current[trim.b] || pps), free = e.altKey;
            let cur;
            if (trim.kind === 'start') cur = {start: clamp(songSnap(c, o.start + dt, free), 0, o.end - 0.25)};
            else if (trim.kind === 'end') cur = {end: clamp(songSnap(c, o.end + dt, free), o.start + 0.25, songLen)};
            else if (trim.kind === 'fadeIn') cur = {fadeIn: +clamp(o.fadeIn + dt, 0, len - o.fadeOut).toFixed(3)};
            else if (trim.kind === 'fadeOut') cur = {fadeOut: +clamp(o.fadeOut - dt, 0, len - o.fadeIn).toFixed(3)};
            else { const st = clamp(songSnap(c, o.start - dt, free), 0, songLen - len); cur = {start: st, end: st + len}; }
            setTrim(t => t && {...t, cur});
        };
        const onUp = () => {
            setTrim(t => {
                if (t && t.cur) {
                    justDragged.current = true; setTimeout(() => { justDragged.current = false; }, 50);
                    const fixed = Object.fromEntries(Object.entries(t.cur).map(([k, v]) => [k, +v.toFixed(4)]));
                    edits.trimMusic(docIn.blocks[t.b].id, t.k, fixed).catch(err => setError(err.message));
                }
                return null;
            });
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [trim && trim.clipId, trim && trim.kind, trim && trim.x0]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Click anywhere in a block's column (cards, waveform, lanes) to put the playhead there (Editor's seek decides where).
    // A faint playhead where a click would land, moved directly (a re-render per mouse move is too heavy here).
    // Zooming or scrolling moves the strip under a still mouse, so the ghost is placed again from the mouse's last spot.
    const ghosts = useRef({}), mouse = useRef(null);
    const placeGhost = () => {
        Object.values(ghosts.current).forEach(g => { if (g) g.style.display = 'none'; });
        const m = mouse.current, target = m && document.elementFromPoint(m.x, m.y), col = target && target.closest('[data-block]');
        if (!col || !strip.current.contains(col)) return;
        const b = +col.dataset.block, g = ghosts.current[b];
        const land = g && onSeek && !trim && !dragMarker && !dragCue && !target.closest('[data-noseek]') && seekLanding(doc, live, b, blockXToT(b, m.x));
        if (!land) return;
        g.style.display = 'block';
        g.style.left = `${land.t * blockPps.current[b]}px`;
    };
    const hoverMove = (e) => { mouse.current = {x: e.clientX, y: e.clientY}; placeGhost(); };
    const hoverLeave = () => { mouse.current = null; placeGhost(); };
    const placeGhostRef = useRef(placeGhost);
    placeGhostRef.current = placeGhost;
    useLayoutEffect(() => { placeGhostRef.current(); }, [pps, tabWidths]);
    useEffect(() => {
        const el = strip.current, onScroll = () => placeGhostRef.current();
        el.addEventListener('scroll', onScroll, {passive: true});
        return () => el.removeEventListener('scroll', onScroll);
    }, []);
    const seekClick = (b) => (e) => {
        if (!onSeek || justDragged.current || (e.target.closest && e.target.closest('[data-noseek]'))) return;
        onSeek(b, +blockXToT(b, e.clientX).toFixed(3));
    };

    // compact: the cards take the height the row leaves after the section tab, the waveform strip and the scrollbar, up to a cap
    const stripH = edit ? 0 : 36;
    const cardH = edit ? 124 : Math.max(60, Math.min(100, (stripHeight || 0) - 22 - 3 - stripH - 10 - 10));
    const lanesH = edit ? LANE_ROWS.reduce((s, l) => s + l.h, 0) : 0;
    // A scene's frame centered in its card at the card's height; a wide card lets it grow a little (cropping top and
    // bottom) but never stretches it further (a missing thumbnail just leaves the card black).
    const THUMB_GROW = 1.3;
    const thumb = (scene, w) => {
        if (!thumbsStamp) return null;
        const natural = cardH * 16 / 9;
        const size = w > natural ? `${Math.min(w, natural * THUMB_GROW)}px auto` : 'auto 100%';
        return <div style={{width: '100%', height: '100%', background: `url(/thumbs/${sceneKey(scene)}.jpg?v=${thumbsStamp}) center / ${size} no-repeat`}}/>;
    };

    return <div style={{display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0}}>
        <style>{TIMELINE_CSS}</style>
        {(edit || error) && <div style={{display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: C.dim, padding: '0 14px 4px'}}>
            {edit && <><span style={{letterSpacing: 1.2, textTransform: 'uppercase'}}>Timeline</span><span>{fmtDur(total)} total</span></>}
            {error && <span style={{color: '#ff6b6b'}}>{error} <button onClick={() => setError(null)} style={{...btn, padding: '0 6px'}}>✕</button></span>}
        </div>}
        {edit && selectedCue && <CueInspector cue={selectedCue} catalog={catalog} onChange={updateCue} onDelete={deleteCue}
            onAudition={() => onAudition && onAudition(selectedCue)} onClose={() => setSelCue(null)}/>}
        <div style={{display: 'flex', flex: 1, minHeight: 0}}>
            {edit && <div style={{width: 64, flexShrink: 0, paddingTop: 22 + cardH + 6, fontSize: 10, color: C.dim, letterSpacing: 0.6, textTransform: 'uppercase'}}>
                {LANE_ROWS.map(l => <div key={l.id} style={{height: l.h, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8}}>{l.label}</div>)}
            </div>}
            <div ref={strip} className="tl-strip" style={{flex: 1, display: 'flex', gap: FOLDER_GAP, overflowX: 'auto', overflowY: 'hidden', padding: '0 14px 10px', alignItems: edit ? 'flex-start' : 'center',
                ...(edit || atEnd ? {} : {WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 90px), transparent)', maskImage: 'linear-gradient(to right, #000 calc(100% - 90px), transparent)'})}}>
                {sections(doc).map(sec => {
                    const liveHere = live && sec.blocks.includes(live.blockIndex);
                    const secDur = sec.blocks.reduce((s, b) => s + layout[b].dur, 0);
                    const pps = folderPps(sec.blocks[0]);
                    return <div key={sec.blocks[0]} style={{display: 'flex', flexDirection: 'column', flexShrink: 0}}>
                        <div ref={el => { tabs.current[sec.blocks[0]] = el; }} style={{alignSelf: 'flex-start', position: 'sticky', left: -14, zIndex: 7, display: 'flex', alignItems: 'center', gap: 7, height: 22, boxSizing: 'border-box',
                            background: `linear-gradient(180deg, ${sec.color}, ${sec.color}d0)`, color: inkOn(sec.color),
                            fontSize: 12.5, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', padding: '3px 8px 2px 11px',
                            borderRadius: '9px 9px 0 0', whiteSpace: 'nowrap', overflow: 'hidden',
                            boxShadow: `inset 0 1px 0 rgba(255,255,255,.35)${liveHere ? `, 0 -3px 16px ${sec.color}99` : ''}`}}>
                            <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{sec.title}</span>
                            <span style={{fontSize: 9, fontWeight: 700, letterSpacing: 0, textTransform: 'none', padding: '1px 6px', borderRadius: 8, background: 'rgba(0,0,0,.22)'}}>{fmtDur(secDur)}</span>
                        </div>
                        <div style={{display: 'flex', gap: SEAM, padding: 3, borderRadius: '0 11px 11px 11px',
                            border: `1px solid ${sec.color}${liveHere ? 'ff' : '66'}`,
                            background: `linear-gradient(180deg, ${sec.color}38 0%, ${sec.color}14 55%, ${sec.color}0a 100%), #101216`,
                            boxShadow: `inset 0 1px 0 ${sec.color}55${liveHere ? `, 0 0 26px ${sec.color}55` : ''}`}}>
                            {sec.blocks.map((b, k) => {
                                const block = doc.blocks[b], L = layout[b], width = Math.max(4, L.dur * pps);
                                blockPps.current[b] = pps;
                                const BL = blockLayout(doc, block), hasMusic = !!BL;
                                return <div key={block.id} data-block={b} data-t0={L.t0} data-dur={L.dur} title={`${block.label} · ${fmtDur(L.dur)}${hasMusic ? '' : ' (no music yet)'}`}
                                    onClick={seekClick(b)} onMouseMove={hoverMove} onMouseLeave={hoverLeave} style={{width, flexShrink: 0, position: 'relative'}}>
                                    {onSeek && <Ghost ref={el => { ghosts.current[b] = el; }}/>}
                                    {playback && playback.block === block.id && <Playhead playback={playback} pps={pps} layout={BL}/>}
                                    {cursor && cursor.block === b && !(playback && playback.block === block.id) && <Cursor t={cursor.t} pps={pps}/>}
                                    {/* seam transition chip: how this block enters */}
                                    {(k > 0 || b > 0) && edit && <button data-noseek="1" onClick={() => cycleTransition(block)} title="Transition into this block (click to change)" style={{
                                        position: 'absolute', left: -SEAM - 12, top: cardH / 2 - 9, zIndex: 3, width: 22, height: 18, padding: 0, fontSize: 10,
                                        borderRadius: 9, border: `1px solid ${C.line}`, background: '#0b0c0f', color: C.text, cursor: 'pointer'}}>
                                        {!block.transition || block.transition.type === 'cut' ? '|' : block.transition.type === 'fade' ? '◢◣' : '✕'}</button>}
                                    {/* scenes */}
                                    <div style={{position: 'relative', height: cardH, borderRadius: 7, overflow: 'hidden',
                                        background: hasMusic ? '#0b0c0f' : `repeating-linear-gradient(135deg, #14171c 0 6px, #0f1115 6px 12px)`}}>
                                        {block.scenes.map((scene, s) => {
                                            const i = flatIndex(doc, b, s);
                                            const start = dragMarker && dragMarker.block === b && dragMarker.i === s && dragMarker.t != null ? dragMarker.t : L.starts[s];
                                            const next = s + 1 < block.scenes.length ? (dragMarker && dragMarker.block === b && dragMarker.i === s + 1 && dragMarker.t != null ? dragMarker.t : L.starts[s + 1]) : L.dur;
                                            const w = Math.max(1, (next - start) * pps - SCENE_GAP);
                                            const isLive = live && live.blockIndex === b && live.sceneIndex === s;
                                            const isSel = selected && selected.blockIndex === b && selected.sceneIndex === s;
                                            return <div key={s} className="tl-scene" data-live={isLive ? '1' : '0'}
                                                title={`#${i} ${block.label} · ${sceneLabel(scene)}`} style={{
                                                    position: 'absolute', left: start * pps, width: w, top: 0, bottom: 0, cursor: 'pointer', overflow: 'hidden', borderRadius: 7,
                                                    background: '#000',     // the thumbnails' own black, so a frame melds into a wide card
                                                    boxShadow: isLive ? `0 0 0 2px ${C.accent}, 0 0 16px ${C.accent}66` : isSel ? '0 0 0 2px #fff' : 'none'}}>
                                                {w > 14 && thumb(scene, w)}
                                                {hasMusic && <ScreenVideo layout={BL} from={start} to={next} pps={pps}/>}
                                                {isLive && w > 30 && <span style={{position: 'absolute', top: 4, left: 4, display: 'flex', alignItems: 'center', gap: 3, fontSize: 8, fontWeight: 800,
                                                    letterSpacing: 1, color: C.bg, background: C.accent, borderRadius: 4, padding: '1px 4px'}}><span className="tl-live-dot" style={{width: 5, height: 5, borderRadius: 3, background: C.bg}}/>LIVE</span>}
                                                {w > 56 && <span style={{position: 'absolute', left: 0, right: 0, bottom: 0, fontSize: 10, padding: '8px 5px 2px', color: '#fff',
                                                    background: 'linear-gradient(transparent, rgba(0,0,0,.9))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{sceneLabel(scene)}</span>}
                                            </div>;
                                        })}
                                    </div>
                                    {/* compact: thin read-only waveform strip */}
                                    {!edit && <div style={{height: stripH, marginTop: 3, borderRadius: 3, overflow: 'hidden', background: hasMusic ? '#0b0c0f' : 'transparent',
                                        border: hasMusic ? 'none' : `1px dashed ${C.line}`, boxSizing: 'border-box'}}>
                                        {hasMusic && <div style={{position: 'relative'}}>
                                            <MusicWave doc={doc} block={block} analyses={analyses} width={BL.total * pps} height={stripH} color={`${sec.color}cc`} grid={false}/>
                                            <MusicSeams layout={BL} starts={L.starts} pps={pps}/></div>}
                                    </div>}
                                    {/* edit: lanes */}
                                    {edit && <div style={{marginTop: 6, height: lanesH, position: 'relative'}}>
                                        <div onDragOver={e => { if (e.dataTransfer.types.includes('application/x-effect')) return; e.preventDefault(); setDropBlock(b); }} onDragLeave={() => setDropBlock(null)} onDrop={dropOnMusic(b)}
                                            className={dropBlock === b ? 'tl-drop' : undefined}
                                            style={{height: LANE_ROWS[0].h - 4, borderRadius: 5, overflow: 'hidden', position: 'relative',
                                                background: hasMusic ? '#0b0c0f' : 'transparent', border: hasMusic ? 'none' : `1px dashed ${C.line}`, boxSizing: 'border-box'}}>
                                            {hasMusic ? <>
                                                <MusicWave doc={doc} block={block} analyses={analyses} width={BL.total * pps} height={LANE_ROWS[0].h - 4} color={`${sec.color}`} grid/>
                                                <BedSpan layout={BL} pps={pps} height={LANE_ROWS[0].h - 4}/>
                                                <MusicSeams layout={BL} starts={L.starts} pps={pps}/>
                                                {L.dur > BL.total + 0.01 && <div title="These scenes come after the music ends" style={{position: 'absolute', top: 0, bottom: 0, left: BL.total * pps, right: 0,
                                                    background: 'repeating-linear-gradient(135deg, #14171c 0 6px, #0f1115 6px 12px)', borderLeft: `1px dashed ${C.line}`}}>
                                                    {(L.dur - BL.total) * pps > 60 && <span style={{fontSize: 10, color: C.dim, padding: 6, display: 'block', whiteSpace: 'nowrap'}}>no music</span>}</div>}
                                                {songClips(BL).map(({clip: c, at, length}, k) => {
                                                    const w = length * pps, x0 = at * pps;
                                                    return <React.Fragment key={k}>
                                                        <ClipHandles clip={c} x={x0} w={w} h={LANE_ROWS[0].h - 4} active={trim && trim.b === b && trim.k === k}
                                                            onGrab={(kind, e) => grabClip(b, k, kind, e)} onRemove={() => edits.removeClip(block, k).catch(err => setError(err.message))}/>
                                                        {w > 80 && <span style={{position: 'absolute', left: x0 + 8, top: 3, fontSize: 10, color: '#fff', textShadow: '0 1px 2px #000', whiteSpace: 'nowrap', pointerEvents: 'none'}}>♪ {c.name}</span>}
                                                    </React.Fragment>;
                                                })}
                                                <button data-noseek="1" onClick={() => edits.patchBlock(block.id, {musicEnd: MUSIC_END_NEXT[block.musicEnd || 'stop']}).catch(err => setError(err.message))}
                                                    title={MUSIC_END_TITLE[block.musicEnd || 'stop'] + ' — click to change'}
                                                    style={{position: 'absolute', left: Math.max(2, BL.total * pps - 74), top: 3, zIndex: 4, fontSize: 9, padding: '1px 6px', borderRadius: 6, cursor: 'pointer',
                                                        border: `1px solid ${block.musicEnd ? C.accent : C.line}`, background: '#000c', color: block.musicEnd ? C.accent : C.dim}}>
                                                    {MUSIC_END_LABEL[block.musicEnd || 'stop']}</button>
                                            </> : width > 70 && <span style={{fontSize: 10, color: C.dim, padding: 6, display: 'block', whiteSpace: 'nowrap'}}>drop a song or clip</span>}
                                        </div>
                                        <div style={{height: LANE_ROWS[1].h, position: 'relative', marginTop: 4}}>
                                            {block.scenes.map((scene, s) => {
                                                if (s === 0) return null;
                                                const fixed = hasMusic && BL.segmentOf(s).open;            // a bed's scenes wait for the game
                                                const t = dragMarker && dragMarker.block === b && dragMarker.i === s && dragMarker.t != null ? dragMarker.t : L.starts[s];
                                                return <div key={s} data-noseek="1" title={fixed ? `${sceneLabel(scene)} — waits for the game (open)` : hasMusic ? `${sceneLabel(scene)} — drag to retime (snaps to beats, ⌥ for free)` : 'add music to time scenes'}
                                                    onMouseDown={e => { if (!hasMusic || fixed) return; e.preventDefault(); setDragMarker({block: b, i: s, t: null}); }}
                                                    style={{position: 'absolute', left: t * pps - 5, top: 2, width: 10, height: LANE_ROWS[1].h - 6, cursor: hasMusic && !fixed ? 'ew-resize' : 'default', opacity: fixed ? 0.5 : 1}}>
                                                    <div style={{margin: '0 auto', width: 2, height: '100%', background: hasMusic ? sec.color : C.line}}/>
                                                    <div style={{position: 'absolute', top: -2, left: 0, width: 10, height: 6, borderRadius: 2, background: hasMusic ? sec.color : C.line}}/>
                                                </div>;
                                            })}
                                        </div>
                                        {LANE_ROWS.slice(2).map(l => l.id === 'lights' ? <div key={l.id}
                                            onDragOver={e => { if (!e.dataTransfer.types.includes('application/x-effect')) return; e.preventDefault(); setDropBlock(`L${b}`); }}
                                            onDragLeave={() => setDropBlock(null)} onDrop={dropOnLights(b)} onMouseDown={e => { if (e.target === e.currentTarget) setSelCue(null); }}
                                            className={dropBlock === `L${b}` ? 'tl-drop' : undefined}
                                            style={{height: l.h, position: 'relative', borderTop: `1px solid ${C.line}55`}}>
                                            {cueSpans(block.cues || [], catalog, (t) => bpmAt(doc, block, t), L.dur).map(sp => {
                                                const dragging = dragCue && dragCue.id === sp.cue.id && dragCue.t != null;
                                                const shift = dragging ? dragCue.t - sp.cue.at : 0;
                                                const isSel = selCue && selCue.id === sp.cue.id;
                                                const hit = sp.kind === 'hit', group = !hit && sp.target !== 'all', top = hit ? 29 : group ? 8 : 4, h = hit ? 20 : group ? 14 : 22;
                                                const w = Math.max(6, (sp.end - sp.start) * pps - 1);
                                                return <div key={sp.cue.id} data-noseek="1" title={`${sp.label}${sp.target !== 'all' ? ` · ${sp.target}` : ''} — drag to move (snaps to beats, ⌥ for free), click to edit`}
                                                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setSelCue({block: b, id: sp.cue.id}); setDragCue({block: b, id: sp.cue.id, x0: e.clientX, at0: sp.cue.at, t: null}); }}
                                                    style={{position: 'absolute', left: (sp.start + shift) * pps, width: w, top, height: h, borderRadius: 4, cursor: 'grab', overflow: 'hidden',
                                                        background: cueSwatch(sp.cue, catalog), boxSizing: 'border-box', zIndex: isSel ? 3 : group ? 2 : 1,
                                                        border: isSel ? '2px solid #fff' : '1px solid #0009', boxShadow: isSel ? `0 0 10px ${C.accent}` : 'none'}}>
                                                    {w > 40 && <span style={{position: 'absolute', left: 4, top: hit ? 2 : group ? 0 : 3, fontSize: 9, fontWeight: 700, color: '#fff', textShadow: '0 0 3px #000, 0 1px 2px #000', whiteSpace: 'nowrap'}}>
                                                        {sp.label}{sp.target !== 'all' ? ` · ${sp.target}` : ''}</span>}
                                                </div>;
                                            })}
                                            {width > 90 && !(block.cues || []).some(c => c.lane === 'lights') && <span style={{position: 'absolute', left: 6, top: 18, fontSize: 10, color: C.dim, pointerEvents: 'none', whiteSpace: 'nowrap'}}>
                                                {hasMusic ? 'beat pulse · drop effects here' : 'drop effects here'}</span>}
                                        </div> : <div key={l.id} style={{height: l.h, position: 'relative', borderTop: `1px solid ${C.line}55`}}>
                                            {(block.cues || []).filter(c => c.lane === l.id).map((c, ci) => <span key={ci} title={JSON.stringify(c)} style={{
                                                position: 'absolute', left: c.at * pps, top: 4, fontSize: 9, padding: '1px 5px', borderRadius: 6, background: sec.color, color: inkOn(sec.color), whiteSpace: 'nowrap'}}>
                                                {c.name || c.look || c.sound || c.lane}</span>)}
                                        </div>)}
                                    </div>}
                                </div>;
                            })}
                        </div>
                    </div>;
                })}
            </div>
        </div>
    </div>;
};

export default Timeline;
